import { entity, persistence } from "simpler-state";
import { appStateType, ToastType } from "../types/global.types";

import { generateId } from "../utils/helpers";

// Uses environment variable - defaults to staging if not set
const baseUrl =
  process.env.REACT_APP_API_BASE_URL || "https://staging-api.pif-app.com/v1";

const getBaseApi = (language = "en") => `${baseUrl}/${language}`;

const defaultState: appStateType = {
  baseApi: getBaseApi(),
  redirectTo: "/dashboard",
  toasts: [],
  loader: {
    active: false,
    theme: "light",
  },
  requesting: {
    caller: "PIF",
    status: false,
  },
  currentMarketplace: "",
  timeFilter: "all_time",
  featureFlag: {
    exportCSV: false,
  },
};

const getPersistedLanguage = (persistedState: Partial<appStateType>) => {
  if (typeof persistedState.baseApi !== "string") return "en";

  return persistedState.baseApi.split("/").filter(Boolean).pop() || "en";
};

const restorePersistedAppState = (value: string): appStateType => {
  const persistedState = JSON.parse(value) as Partial<appStateType>;

  return {
    ...defaultState,
    ...persistedState,
    baseApi: getBaseApi(getPersistedLanguage(persistedState)),
  };
};

export const appEntity = entity(defaultState, [
  persistence("pif-dash-app", {
    deserializeFn: restorePersistedAppState,
  }),
]);

// actions
export const setPendingRedirect = (path: string) =>
  appEntity.set((currentState) => ({
    ...currentState,
    redirectTo: path.startsWith("/auth") ? "/dashboard" : path,
  }));

export const setApiBase = (language: string) =>
  appEntity.set((currentState) => ({
    ...currentState,
    baseApi: getBaseApi(language),
  }));

export const clearPendingRedirect = () =>
  appEntity.set((currentState) => ({
    ...currentState,
    redirectTo: "/dashboard",
  }));

export const showGlobalLoader = (theme: "light" | "dark" = "light") =>
  appEntity.set((currenState) => ({
    ...currenState,
    loader: {
      active: true,
      theme,
    },
  }));
export const hideGlobalLoader = () =>
  appEntity.set((currenState) => ({
    ...currenState,
    loader: {
      active: false,
      theme: "light",
    },
  }));

export const removeToast = (toastId: string, duration = 10000) => {
  setTimeout(() => {
    appEntity.set((currentState) => {
      return {
        ...currentState,
        toasts: currentState.toasts.map((toast) => {
          if (toast.id === toastId) toast.active = false;
          return toast;
        }),
      };
    });
    appEntity.set((currentState) => ({
      ...currentState,
      toasts: currentState.toasts.filter((toast) => toast.id !== toastId),
    }));
  }, duration);
};

export const toast = (
  message: string,
  type: ToastType["type"] = "success",
  body?: string
) => {
  const toastId = generateId();

  appEntity.set((currentState) => ({
    ...currentState,
    toasts: [
      ...currentState.toasts,
      {
        message,
        ...(body && { body }),
        id: toastId,
        active: true,
        type,
      },
    ],
  }));
  removeToast(toastId, 5000);
};

export const requestStart = (caller: "global" | string = "PIF") =>
  appEntity.set((state) => ({
    ...state,
    requesting: { caller, status: true },
  }));

export const requestEnd = () =>
  appEntity.set((state) => ({ ...state, requesting: defaultState.requesting }));

export const setCurrentMarketplace = (marketplace: string) =>
  appEntity.set((state) => ({ ...state, currentMarketplace: marketplace }));

export const setTimeFilter = (time: appStateType["timeFilter"]) =>
  appEntity.set((state) => ({ ...state, timeFilter: time }));
